import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { AlertCircle, X, Zap } from "lucide-react";
import { deriveStepInputFromSource } from "@bliks-io/contracts";
import { InputFormDataSchema, toStartEventContent, toStepInput } from "./schemas/inputFormSchema";
import { Alert, AlertDescription } from "../../base/alert/alert";
import { Dialog } from "../../base/dialog/dialog";
import { DialogShell } from "../dialog-shell/DialogShell";
import { DialogShellHeader } from "../dialog-shell/DialogShellHeader";
import { DialogShellBody } from "../dialog-shell/DialogShellBody";
import { DialogShellFooter } from "../dialog-shell/DialogShellFooter";
import { ReadOnlyField } from "../dialog-shell/ReadOnlyField";
import { InfoHint } from "../info-hint/info-hint";
import { Input } from "../../base/input/input";
import { Textarea } from "../../base/textarea/textarea";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxGroup, ComboboxInput, ComboboxItem, ComboboxList, ComboboxTrigger } from "../combobox/combobox";
import { Field, FieldContent, FieldError, FieldLabel, FieldSeparator } from "../field/field";
import { ACTION_TYPE_ICONS, STEP_FIELD_ICONS } from "../step-detail/stepFieldIcons";
import { StepNumberBadge } from "../step-detail/StepNumberBadge";
const EMPTY_DEFAULTS = {
    inputType: "event",
    type: "",
    name: "",
    description: ""
};
function populateFormFromInput(input) {
    if ("eventType" in input) {
        return {
            inputType: "event",
            type: input.eventType,
            name: input.name,
            description: input.description
        };
    }
    // Derived step inputs (`notRemovable: true`) store only the link — display
    // values are hydrated at fetch time and may be undefined if the source can't
    // be resolved. Fall back to empty strings so the form (which expects strings)
    // doesn't blow up; the read-only branch surfaces the dash placeholder.
    switch(input.inputType){
        case "event":
            return {
                inputType: "event",
                type: input.type ?? "",
                name: input.name ?? "",
                description: input.description ?? ""
            };
        case "previous_output":
            return {
                inputType: "previous_output",
                name: input.name ?? "",
                description: input.description ?? "",
                ...input.sourceSteps?.length && {
                    sourceSteps: input.sourceSteps
                }
            };
        default:
            return EMPTY_DEFAULTS;
    }
}
function getDialogTitle(isInitialEventMode, isEditing, isReadOnly, t) {
    if (isInitialEventMode) {
        return isEditing ? t("viralRecording.modals.eventModal.editEventTitle") || "Edit Start Event" : t("viralRecording.modals.eventModal.createEventTitle") || "Create Start Event";
    }
    if (isReadOnly) {
        return t("viralRecording.modals.inputModal.viewTitle") || "View Input";
    }
    return isEditing ? t("viralRecording.modals.inputModal.editTitle") || "Edit Input" : t("viralRecording.modals.inputModal.addTitle") || "Add Input";
}
function getDialogDescription(isInitialEventMode, t) {
    if (isInitialEventMode) {
        return t("viralRecording.modals.eventModal.startEventDescription") || "Define what triggers the start of this process";
    }
    return t("viralRecording.modals.inputModal.description") || "Define what information or event triggers this step";
}
function EventTypeCombobox({ control, name, label, placeholder, required, eventTypes }) {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: name,
        render: ({ field, fieldState })=>{
            const hasError = !!fieldState.error;
            const selectedEvent = eventTypes.find((evt)=>evt.id === field.value);
            return /*#__PURE__*/ _jsxs(Field, {
                "data-invalid": hasError,
                children: [
                    /*#__PURE__*/ _jsxs(FieldLabel, {
                        htmlFor: name,
                        children: [
                            label,
                            required && /*#__PURE__*/ _jsx("span", {
                                className: "text-destructive ml-1",
                                children: "*"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(FieldContent, {
                        children: [
                            /*#__PURE__*/ _jsxs(Combobox, {
                                value: field.value,
                                onValueChange: (value)=>{
                                    if (typeof value === "string") field.onChange(value);
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(ComboboxTrigger, {
                                        id: name,
                                        placeholder: placeholder,
                                        children: selectedEvent ? /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(selectedEvent.icon, {
                                                    size: 16
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: t(selectedEvent.labelKey)
                                                })
                                            ]
                                        }) : null
                                    }),
                                    /*#__PURE__*/ _jsx(ComboboxContent, {
                                        children: /*#__PURE__*/ _jsxs(ComboboxList, {
                                            children: [
                                                /*#__PURE__*/ _jsx(ComboboxEmpty, {
                                                    children: t("common.noOptionsAvailable")
                                                }),
                                                eventTypes.map((event)=>{
                                                    const Icon = event.icon;
                                                    return /*#__PURE__*/ _jsx(ComboboxItem, {
                                                        value: event.id,
                                                        children: /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center gap-2 flex-1",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Icon, {
                                                                    size: 16
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    children: t(event.labelKey)
                                                                })
                                                            ]
                                                        })
                                                    }, event.id);
                                                })
                                            ]
                                        })
                                    })
                                ]
                            }),
                            fieldState.error?.message && /*#__PURE__*/ _jsx(FieldError, {
                                children: t(fieldState.error.message)
                            })
                        ]
                    })
                ]
            });
        }
    });
}
function TextField({ control, name, label, placeholder, required, disabled }) {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: name,
        render: ({ field, fieldState })=>/*#__PURE__*/ _jsxs(Field, {
                "data-invalid": !!fieldState.error,
                children: [
                    /*#__PURE__*/ _jsxs(FieldLabel, {
                        children: [
                            label,
                            required && /*#__PURE__*/ _jsx("span", {
                                className: "text-destructive ml-1",
                                children: "*"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(FieldContent, {
                        children: [
                            /*#__PURE__*/ _jsx(Input, {
                                ...field,
                                value: field.value ?? "",
                                placeholder: placeholder,
                                disabled: disabled,
                                "aria-invalid": !!fieldState.error
                            }),
                            fieldState.error?.message && /*#__PURE__*/ _jsx(FieldError, {
                                children: t(fieldState.error.message)
                            })
                        ]
                    })
                ]
            })
    });
}
function TextAreaField({ control, name, label, placeholder, required, rows, disabled }) {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: name,
        render: ({ field, fieldState })=>/*#__PURE__*/ _jsxs(Field, {
                "data-invalid": !!fieldState.error,
                children: [
                    /*#__PURE__*/ _jsxs(FieldLabel, {
                        children: [
                            label,
                            required && /*#__PURE__*/ _jsx("span", {
                                className: "text-destructive ml-1",
                                children: "*"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(FieldContent, {
                        children: [
                            /*#__PURE__*/ _jsx(Textarea, {
                                ...field,
                                value: field.value ?? "",
                                rows: rows,
                                placeholder: placeholder,
                                disabled: disabled,
                                "aria-invalid": !!fieldState.error
                            }),
                            fieldState.error?.message && /*#__PURE__*/ _jsx(FieldError, {
                                children: t(fieldState.error.message)
                            })
                        ]
                    })
                ]
            })
    });
}
function EventSection({ control, isInitialEventMode, eventTypes, t, isReadOnly, formValues }) {
    // Resolve the trigger ("Auslöser") to its localized label. Read-only views must
    // never surface the raw eventType id (e.g. "none"). The placeholder "none"
    // type carries no meaningful trigger, so it renders empty (—) rather than its
    // "Manuelle Aktion" label, which would be misleading for a step input.
    const eventTypeLabelKey = formValues && "type" in formValues && formValues.type && formValues.type !== "none" ? eventTypes.find((opt)=>opt.id === formValues.type)?.labelKey : undefined;
    const triggerDisplayValue = eventTypeLabelKey ? t(eventTypeLabelKey) : undefined;
    if (isReadOnly && formValues) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-4",
            children: [
                !isInitialEventMode && /*#__PURE__*/ _jsx(FieldSeparator, {
                    children: t("viralRecording.modals.inputModal.eventDetails") || "Event Details"
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 gap-4 md:grid-cols-2",
                    children: [
                        /*#__PURE__*/ _jsx(ReadOnlyField, {
                            variant: "stacked",
                            label: t("viralRecording.modals.eventModal.trigger") || "Trigger",
                            value: triggerDisplayValue
                        }),
                        /*#__PURE__*/ _jsx(ReadOnlyField, {
                            variant: "stacked",
                            label: t("viralRecording.modals.eventModal.eventName") || "Event name",
                            value: formValues.name
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(ReadOnlyField, {
                    variant: "stacked",
                    label: t("common.Description") || "Description",
                    value: formValues.description
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-4",
        children: [
            !isInitialEventMode && /*#__PURE__*/ _jsx(FieldSeparator, {
                children: t("viralRecording.modals.inputModal.eventDetails") || "Event Details"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 gap-4 md:grid-cols-2",
                children: [
                    /*#__PURE__*/ _jsx(EventTypeCombobox, {
                        control: control,
                        name: "type",
                        label: t("viralRecording.modals.eventModal.trigger") || "Trigger",
                        placeholder: t("viralRecording.modals.eventModal.selectTrigger") || "Select trigger",
                        required: true,
                        eventTypes: eventTypes
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        control: control,
                        name: "name",
                        label: t("viralRecording.modals.eventModal.eventName") || "Event name",
                        placeholder: t("viralRecording.modals.eventModal.enterEventName") || "Enter event name",
                        required: true
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(TextAreaField, {
                control: control,
                name: "description",
                label: t("common.Description") || "Description",
                placeholder: t("viralRecording.modals.eventModal.describeWhatTriggers") || "Describe what triggers this event",
                rows: 4,
                required: true
            })
        ]
    });
}
function InputTypeSection({ control, inputType, isReadOnly, isEditing, t }) {
    const inputTypeOptions = [
        {
            value: "event",
            label: t("viralRecording.modals.inputModal.inputTypeEvent") || "Event (Trigger)"
        },
        {
            value: "previous_output",
            label: t("viralRecording.modals.inputModal.inputTypePreviousOutput") || "Previous Output (Information)"
        }
    ];
    if (isReadOnly) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-4",
            children: [
                /*#__PURE__*/ _jsx(FieldSeparator, {
                    children: t("viralRecording.modals.inputModal.inputTypeSection") || "Input Type"
                }),
                /*#__PURE__*/ _jsx(ReadOnlyField, {
                    variant: "stacked",
                    label: t("viralRecording.modals.inputModal.inputType") || "Type",
                    value: inputTypeOptions.find((o)=>o.value === inputType)?.label
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-4",
        children: [
            /*#__PURE__*/ _jsx(FieldSeparator, {
                children: t("viralRecording.modals.inputModal.inputTypeSection") || "Input Type"
            }),
            /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "inputType",
                render: ({ field, fieldState })=>{
                    const selectedOption = inputTypeOptions.find((o)=>o.value === field.value);
                    return /*#__PURE__*/ _jsxs(Field, {
                        "data-invalid": !!fieldState.error,
                        children: [
                            /*#__PURE__*/ _jsxs(FieldLabel, {
                                htmlFor: "inputType",
                                children: [
                                    t("viralRecording.modals.inputModal.inputType") || "Type",
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-destructive ml-1",
                                        children: "*"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(FieldContent, {
                                children: [
                                    /*#__PURE__*/ _jsxs(Combobox, {
                                        value: field.value,
                                        onValueChange: (value)=>{
                                            if (typeof value === "string") field.onChange(value);
                                        },
                                        disabled: isEditing,
                                        children: [
                                            /*#__PURE__*/ _jsx(ComboboxTrigger, {
                                                id: "inputType",
                                                placeholder: t("viralRecording.modals.inputModal.selectInputType") || "Select input type",
                                                children: selectedOption ? /*#__PURE__*/ _jsx("span", {
                                                    children: selectedOption.label
                                                }) : null
                                            }),
                                            /*#__PURE__*/ _jsx(ComboboxContent, {
                                                children: /*#__PURE__*/ _jsx(ComboboxList, {
                                                    children: inputTypeOptions.map((option)=>/*#__PURE__*/ _jsx(ComboboxItem, {
                                                            value: option.value,
                                                            children: option.label
                                                        }, option.value))
                                                })
                                            })
                                        ]
                                    }),
                                    fieldState.error?.message && /*#__PURE__*/ _jsx(FieldError, {
                                        children: t(fieldState.error.message)
                                    })
                                ]
                            })
                        ]
                    });
                }
            })
        ]
    });
}
function sourceStepToOptionValue(src) {
    return src.ruleIds?.length ? `${src.stepId}:${[
        ...src.ruleIds
    ].sort().join(",")}` : src.stepId;
}
function buildStepOptions(processSteps, t) {
    return processSteps.flatMap((step)=>{
        const label = step.generatedName || t("viralRecording.labels.untitled");
        if (step.actionType === "task" && step.task?.output) {
            const { name, description } = deriveStepInputFromSource(step);
            return [
                {
                    value: sourceStepToOptionValue({
                        stepId: step._id
                    }),
                    label,
                    output: step.task.output,
                    name,
                    description,
                    stepId: step._id,
                    actionType: "task",
                    stepNumber: step.stepNumber
                }
            ];
        }
        if (step.actionType === "decision" && step.rules) {
            const rulesByOutput = new Map();
            for (const rule of step.rules){
                if (!rule.output || !rule._id) continue;
                const existing = rulesByOutput.get(rule.output);
                if (existing) existing.push(rule._id);
                else rulesByOutput.set(rule.output, [
                    rule._id
                ]);
            }
            return Array.from(rulesByOutput.entries()).map(([output, ruleIds])=>{
                // Same shared derivation the editor and backend hydration use, so the
                // stored input reads identically: the source's generated title /
                // description with the chosen branch appended.
                const { name, description } = deriveStepInputFromSource(step, ruleIds[0]);
                return {
                    value: sourceStepToOptionValue({
                        stepId: step._id,
                        ruleIds
                    }),
                    label,
                    output,
                    name,
                    description,
                    stepId: step._id,
                    ruleIds,
                    actionType: "decision",
                    stepNumber: step.stepNumber
                };
            });
        }
        return [];
    });
}
function StepRecordingSelect({ options, value, onValueChange, placeholder, searchPlaceholder, emptyMessage, disabled, label, labelHint }) {
    const [searchQuery, setSearchQuery] = useState("");
    const filteredOptions = useMemo(()=>{
        if (!searchQuery) return options;
        const q = searchQuery.toLowerCase();
        return options.filter((opt)=>opt.label.toLowerCase().includes(q) || opt.description?.toLowerCase().includes(q) || opt.output?.toLowerCase().includes(q));
    }, [
        options,
        searchQuery
    ]);
    const selectedOption = useMemo(()=>options.find((opt)=>opt.value === value) ?? null, [
        options,
        value
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            label && /*#__PURE__*/ _jsxs("label", {
                className: "mb-1.5 flex items-center gap-1.5 text-sm font-medium",
                children: [
                    label,
                    labelHint && /*#__PURE__*/ _jsx(InfoHint, {
                        content: labelHint
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Combobox, {
                value: value ?? undefined,
                onValueChange: (v)=>onValueChange(typeof v === "string" ? v : null),
                onClear: ()=>onValueChange(null),
                disabled: disabled,
                children: [
                    /*#__PURE__*/ _jsx(ComboboxTrigger, {
                        placeholder: placeholder,
                        allowClear: !!value,
                        children: selectedOption && /*#__PURE__*/ _jsxs("span", {
                            className: "flex min-w-0 items-center gap-1.5",
                            children: [
                                selectedOption.stepNumber != null && /*#__PURE__*/ _jsx(StepNumberBadge, {
                                    number: selectedOption.stepNumber
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "truncate",
                                    children: selectedOption.label
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs(ComboboxContent, {
                        children: [
                            /*#__PURE__*/ _jsx(ComboboxInput, {
                                placeholder: searchPlaceholder,
                                value: searchQuery,
                                onValueChange: setSearchQuery
                            }),
                            /*#__PURE__*/ _jsxs(ComboboxList, {
                                children: [
                                    /*#__PURE__*/ _jsx(ComboboxEmpty, {
                                        children: emptyMessage
                                    }),
                                    /*#__PURE__*/ _jsx(ComboboxGroup, {
                                        children: filteredOptions.map((option)=>{
                                            const Icon = ACTION_TYPE_ICONS[option.actionType];
                                            return /*#__PURE__*/ _jsx(ComboboxItem, {
                                                value: option.value,
                                                children: /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-start gap-2.5",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Icon, {
                                                            className: "mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex min-w-0 flex-col",
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("span", {
                                                                    className: "flex items-center gap-1.5 font-medium",
                                                                    children: [
                                                                        option.stepNumber != null && /*#__PURE__*/ _jsx(StepNumberBadge, {
                                                                            number: option.stepNumber
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "truncate",
                                                                            children: option.label
                                                                        })
                                                                    ]
                                                                }),
                                                                option.description && option.description !== option.output && /*#__PURE__*/ _jsx("span", {
                                                                    className: "line-clamp-2 text-xs text-muted-foreground/70",
                                                                    children: option.description
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-xs text-muted-foreground",
                                                                    children: option.output
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            }, option.value);
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
function PreviousOutputSection({ control, isReadOnly, processSteps, setValue, watch, existingInputs, editingInputIndex }) {
    const { t } = useTranslation();
    const sourceSteps = watch("sourceSteps");
    const primarySource = sourceSteps?.[0];
    const stepOptions = useMemo(()=>buildStepOptions(processSteps || [], t), [
        processSteps,
        t
    ]);
    const usedOptionValues = useMemo(()=>{
        const used = new Set();
        if (!existingInputs) return used;
        existingInputs.forEach((inp, idx)=>{
            if (idx === editingInputIndex) return;
            if (inp.inputType !== "previous_output") return;
            if (!inp.sourceSteps?.length) return;
            for (const src of inp.sourceSteps){
                used.add(sourceStepToOptionValue(src));
            }
        });
        return used;
    }, [
        existingInputs,
        editingInputIndex
    ]);
    const filteredStepOptions = useMemo(()=>stepOptions.filter((opt)=>!usedOptionValues.has(opt.value)), [
        stepOptions,
        usedOptionValues
    ]);
    const selectedOption = useMemo(()=>{
        if (!primarySource?.stepId) return null;
        if (primarySource.ruleIds?.length) {
            return stepOptions.find((opt)=>opt.stepId === primarySource.stepId && primarySource.ruleIds.every((id)=>opt.ruleIds?.includes(id))) ?? null;
        }
        return stepOptions.find((opt)=>opt.value === primarySource.stepId) ?? null;
    }, [
        primarySource,
        stepOptions
    ]);
    const isLinkedToStep = !!selectedOption;
    const hasStaleLink = !!primarySource?.stepId && !selectedOption;
    const allStepsAlreadyLinked = stepOptions.length > 0 && filteredStepOptions.length === 0;
    const emptyMessage = allStepsAlreadyLinked ? t("viralRecording.modals.inputModal.allStepsAlreadyLinked") : t("viralRecording.modals.inputModal.noCompletedSteps");
    const handleStepSelect = (value)=>{
        if (!value) {
            setValue("sourceSteps", undefined);
            setValue("name", "");
            setValue("description", "");
            return;
        }
        const option = stepOptions.find((opt)=>opt.value === value);
        if (!option) return;
        setValue("sourceSteps", [
            {
                stepId: option.stepId,
                ...option.ruleIds?.length ? {
                    ruleIds: option.ruleIds
                } : {}
            }
        ]);
        setValue("name", option.name);
        setValue("description", option.description);
    };
    const showReadOnlyView = isReadOnly || isLinkedToStep;
    const selectedValue = selectedOption?.value ?? null;
    const selectedLabel = selectedOption?.label;
    const nameValue = watch("name");
    const descriptionValue = watch("description");
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-4",
        children: [
            /*#__PURE__*/ _jsx(FieldSeparator, {
                children: t("viralRecording.modals.inputModal.informationDetails")
            }),
            (processSteps && processSteps.length > 0 || isReadOnly && isLinkedToStep || hasStaleLink) && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    showReadOnlyView ? /*#__PURE__*/ _jsx(ReadOnlyField, {
                        variant: "stacked",
                        label: t("viralRecording.modals.inputModal.linkToProcessStep"),
                        value: selectedLabel
                    }) : /*#__PURE__*/ _jsx(StepRecordingSelect, {
                        options: filteredStepOptions,
                        value: selectedValue,
                        onValueChange: handleStepSelect,
                        label: t("viralRecording.modals.inputModal.linkToProcessStep"),
                        labelHint: t("viralRecording.modals.inputModal.linkToProcessStepHint"),
                        placeholder: t("viralRecording.modals.inputModal.selectProcessStep"),
                        searchPlaceholder: t("viralRecording.modals.inputModal.selectProcessStep"),
                        emptyMessage: emptyMessage,
                        disabled: isReadOnly
                    }),
                    hasStaleLink && /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: t("viralRecording.modals.inputModal.linkedStepNotFound")
                    })
                ]
            }),
            showReadOnlyView ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(ReadOnlyField, {
                        variant: "stacked",
                        label: t("viralRecording.modals.inputModal.informationName"),
                        value: nameValue
                    }),
                    /*#__PURE__*/ _jsx(ReadOnlyField, {
                        variant: "stacked",
                        label: t("common.Description"),
                        value: descriptionValue
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(TextField, {
                        control: control,
                        name: "name",
                        label: t("viralRecording.modals.inputModal.informationName"),
                        placeholder: t("viralRecording.modals.inputModal.enterInformationName"),
                        required: true,
                        disabled: hasStaleLink
                    }),
                    /*#__PURE__*/ _jsx(TextAreaField, {
                        control: control,
                        name: "description",
                        label: t("common.Description"),
                        placeholder: t("viralRecording.modals.inputModal.describeInformation"),
                        rows: 4,
                        required: true,
                        disabled: hasStaleLink
                    })
                ]
            })
        ]
    });
}
/**
 * Form dialog for creating/editing unified inputs (events and previous outputs).
 *
 * Supports two modes:
 * - 'step-input': Full dialog with inputType selector (default)
 * - 'initial-event': Simplified for initial events only
 */ export function InputFormDialog({ open, onOpenChange, ...props }) {
    const [formKey, setFormKey] = useState(0);
    const [prevOpen, setPrevOpen] = useState(false);
    if (open !== prevOpen) {
        setPrevOpen(open);
        if (open) setFormKey((k)=>k + 1);
    }
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsx(DialogShell, {
            size: "lg",
            children: /*#__PURE__*/ _jsx(InputFormDialogContent, {
                onOpenChange: onOpenChange,
                ...props
            }, formKey)
        })
    });
}
function InputFormDialogContent({ onOpenChange, onSubmit, input, mode = "step-input", eventTypes = [], isSubmitting = false, error, onErrorDismiss, onSetErrorReady, processSteps, readOnly, existingInputs, editingInputIndex }) {
    const { t } = useTranslation();
    const isEditing = !!input;
    const isInitialEventMode = mode === "initial-event";
    const [isReadOnly] = useState(()=>readOnly || !!input && "notRemovable" in input && input.notRemovable === true);
    const { handleSubmit, control, watch, setError, setValue } = useForm({
        resolver: zodResolver(InputFormDataSchema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: input ? populateFormFromInput(input) : EMPTY_DEFAULTS
    });
    const setErrorExposedRef = useRef(false);
    if (onSetErrorReady && !setErrorExposedRef.current) {
        onSetErrorReady(setError);
        setErrorExposedRef.current = true;
    }
    const formValues = watch();
    const inputType = formValues.inputType;
    const handleFormSubmit = (formData)=>{
        onSubmit(isInitialEventMode ? toStartEventContent(formData) : toStepInput(formData));
    };
    const DialogIcon = isInitialEventMode ? Zap : STEP_FIELD_ICONS.input;
    return /*#__PURE__*/ _jsxs("form", {
        noValidate: true,
        onSubmit: handleSubmit(handleFormSubmit),
        className: "flex flex-1 min-h-0 flex-col",
        children: [
            /*#__PURE__*/ _jsx(DialogShellHeader, {
                icon: /*#__PURE__*/ _jsx(DialogIcon, {
                    className: "h-5 w-5"
                }),
                iconTone: isInitialEventMode ? "green" : "neutral",
                title: getDialogTitle(isInitialEventMode, isEditing, isReadOnly, t),
                subtitle: getDialogDescription(isInitialEventMode, t),
                onClose: ()=>onOpenChange(false)
            }),
            /*#__PURE__*/ _jsxs(DialogShellBody, {
                children: [
                    error && /*#__PURE__*/ _jsxs(Alert, {
                        variant: "destructive",
                        children: [
                            /*#__PURE__*/ _jsx(AlertCircle, {
                                className: "h-4 w-4"
                            }),
                            /*#__PURE__*/ _jsxs(AlertDescription, {
                                className: "flex items-start justify-between gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "flex-1",
                                        children: error
                                    }),
                                    onErrorDismiss && /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: onErrorDismiss,
                                        className: "shrink-0 rounded-xs opacity-70 transition-opacity hover:opacity-100",
                                        "aria-label": t("common.dismiss"),
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "h-4 w-4"
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col gap-8",
                        children: [
                            !isInitialEventMode && /*#__PURE__*/ _jsx(InputTypeSection, {
                                control: control,
                                inputType: inputType,
                                isReadOnly: isReadOnly,
                                isEditing: isEditing,
                                t: t
                            }),
                            (isInitialEventMode || inputType === "event") && /*#__PURE__*/ _jsx(EventSection, {
                                control: control,
                                isInitialEventMode: isInitialEventMode,
                                eventTypes: eventTypes,
                                t: t,
                                isReadOnly: isReadOnly,
                                formValues: formValues
                            }),
                            !isInitialEventMode && inputType === "previous_output" && /*#__PURE__*/ _jsx(PreviousOutputSection, {
                                control: control,
                                isReadOnly: isReadOnly,
                                processSteps: processSteps,
                                setValue: setValue,
                                watch: watch,
                                existingInputs: existingInputs,
                                editingInputIndex: editingInputIndex
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DialogShellFooter, {
                secondary: {
                    label: t("common.cancel"),
                    onClick: ()=>onOpenChange(false),
                    disabled: isSubmitting
                },
                primary: isReadOnly ? undefined : {
                    label: isEditing ? t("common.Save") : t("common.Create"),
                    type: "submit",
                    loading: isSubmitting
                }
            })
        ]
    });
}
export default InputFormDialog;

//# sourceMappingURL=InputFormDialog.js.map